
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import AmountDisplayerFilterable from '@/components/reports-v3/components/filterables/AmountDisplayerFilterable.vue';
import moment from 'moment';
import FilterWidget from '../shared/FilterWidget.vue';
import {PermissionsGroup} from '@/store/models.def';
import { roundToFixed } from '@/util/number';
import gdbx from '@/store/modules/gdbx';
import IncludeCreditNoteToggle from '@/components/IncludeCreditNoteToggle.vue';
import IncludeDebitNoteToggle from '@/components/IncludeDebitNoteToggle.vue';
import SlPhModuleOnlyToggle from '@/components/SlPhModuleOnlyToggle.vue';
import settingx from '@/store/modules/settingx';
import {getTableName} from '@/worker/db/sqlHelpers';

@Component({
	components: {
		AmountDisplayerFilterable,
		IncludeCreditNoteToggle,
		IncludeDebitNoteToggle,
		SlPhModuleOnlyToggle,
	},
})
export default class CustomerYtdRevenueContributionNumberDisplayer extends FilterWidget {
	@Prop({ default: '' }) public defaultCustomer!: string | null;

	public filterIds: Array<
		| 'date'
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
	> = [];

	public salesPercentage = '' || 0;
  public amount = '';

	public get permissionIds(): PermissionsGroup[] {
		return ['customers', 'sales'];
	}

	public get currentYear() {
		return moment(this.selectedAsOfDate).year();
	}

	public get currency() {
		return gdbx.currencySymbol;
	}

	public get decimal() {
		return gdbx.numDecimal;
	}

	public get includeCreditNote() {
		return settingx.now.includeCreditNote;
	}

	public get includeDebitNote() {
		return settingx.now.includeDebitNote;
	}

	public get slPhModuleOnly() {
		return settingx.now.slPhModuleOnly;
	}

	public get expensiveHook() {
		const {
			selectedDateRange,
			selectedAsOfDate,
			selectedAgents,
			selectedStockItems,
			currentYear,
			defaultCustomer,
			includeCreditNote,
			includeDebitNote,
			slPhModuleOnly,
		} = this;
		return JSON.stringify([
			selectedDateRange,
			selectedAsOfDate,
			selectedAgents,
			selectedStockItems,
			currentYear,
			defaultCustomer,
			includeCreditNote,
			includeDebitNote,
			slPhModuleOnly,
		]);
	}

	public async expensiveCalc() {
		this.generateLoadingText(0.2, 0.3);
		const jobId = this.currentJobId;

		const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
		const [dateStart, dateEnd] = this.selectedDateRange;

		let customerFilter = '';
		if (this.defaultCustomer) {
			customerFilter = ` AND "CODE" = '${escapeStr(this.defaultCustomer)}'`;
		}

		let totalSalesAmount = 0;
		let customerSalesAmount = 0;

		if (this.slPhModuleOnly) {
			const salesTable = getTableName('sales');
			const cnTable = getTableName('creditNotes');
			const docTypes = this.includeDebitNote ? '\'IV\',\'CS\',\'DN\'' : '\'IV\',\'CS\'';

			const queryHead = await this._ensureTablesLoaded(['sales', 'creditNotes']);
			this.checkInterrupted(jobId);
			this.generateLoadingText(0.3, 0.5);

			const [totalResult, customerResult] = await Promise.all([
				queryHead.rawQuery(`
					SELECT SUM(CASE WHEN "DOCTYPE" IN (${docTypes}) THEN TRY_CAST("AMOUNT" AS DOUBLE) ELSE 0 END) as sales_amt,
						SUM(TRY_CAST("AMOUNT" AS DOUBLE)) as cn_amt
					FROM (
						SELECT "DOCTYPE", "AMOUNT", 'S' as src FROM ${salesTable}
						WHERE TRY_CAST("DOCDATE" AS BIGINT) BETWEEN ${dateStart} AND ${dateEnd}
							AND "DOCTYPE" IN (${docTypes})
						UNION ALL
						SELECT 'CN_TOTAL' as "DOCTYPE", "AMOUNT", 'C' as src FROM ${cnTable}
						WHERE TRY_CAST("DOCDATE" AS BIGINT) BETWEEN ${dateStart} AND ${dateEnd}
					) sub
					GROUP BY src
				`),
				queryHead.rawQuery(`
					SELECT SUM(CASE WHEN "DOCTYPE" IN (${docTypes}) THEN TRY_CAST("AMOUNT" AS DOUBLE) ELSE 0 END) as sales_amt,
						SUM(TRY_CAST("AMOUNT" AS DOUBLE)) as cn_amt
					FROM (
						SELECT "DOCTYPE", "AMOUNT", 'S' as src FROM ${salesTable}
						WHERE TRY_CAST("DOCDATE" AS BIGINT) BETWEEN ${dateStart} AND ${dateEnd}
							AND "DOCTYPE" IN (${docTypes})
							${customerFilter}
						UNION ALL
						SELECT 'CN_TOTAL' as "DOCTYPE", "AMOUNT", 'C' as src FROM ${cnTable}
						WHERE TRY_CAST("DOCDATE" AS BIGINT) BETWEEN ${dateStart} AND ${dateEnd}
							${customerFilter}
					) sub
					GROUP BY src
				`),
			]);
			this.checkInterrupted(jobId);

			let totalSales = 0;
			let totalCN = 0;
			let custSales = 0;
			let custCN = 0;
			for (const r of totalResult) {
				if (r.src === 'S') { totalSales = Number(r.sales_amt) || 0; }
				if (r.src === 'C') { totalCN = Number(r.cn_amt) || 0; }
			}
			for (const r of customerResult) {
				if (r.src === 'S') { custSales = Number(r.sales_amt) || 0; }
				if (r.src === 'C') { custCN = Number(r.cn_amt) || 0; }
			}
			totalSalesAmount = totalSales - (this.includeCreditNote ? totalCN : 0);
			customerSalesAmount = custSales - (this.includeCreditNote ? custCN : 0);
		} else {
			const arTable = getTableName('customerTrades');

			const queryHead = await this._ensureTablesLoaded(['customerTrades']);
			this.checkInterrupted(jobId);
			this.generateLoadingText(0.3, 0.5);

			const [totalResult, customerResult] = await Promise.all([
				queryHead.rawQuery(`
					SELECT
						SUM(CASE WHEN "DOCTYPE" = 'IV' THEN TRY_CAST("LOCALAMOUNT" AS DOUBLE) ELSE 0 END) as iv_amt,
						SUM(CASE WHEN "DOCTYPE" = 'CN' THEN TRY_CAST("LOCALAMOUNT" AS DOUBLE) ELSE 0 END) as cn_amt,
						SUM(CASE WHEN "DOCTYPE" = 'DN' THEN TRY_CAST("LOCALAMOUNT" AS DOUBLE) ELSE 0 END) as dn_amt
					FROM ${arTable}
					WHERE TRY_CAST("DOCDATE" AS BIGINT) BETWEEN ${dateStart} AND ${dateEnd}
				`),
				queryHead.rawQuery(`
					SELECT
						SUM(CASE WHEN "DOCTYPE" = 'IV' THEN TRY_CAST("LOCALAMOUNT" AS DOUBLE) ELSE 0 END) as iv_amt,
						SUM(CASE WHEN "DOCTYPE" = 'CN' THEN TRY_CAST("LOCALAMOUNT" AS DOUBLE) ELSE 0 END) as cn_amt,
						SUM(CASE WHEN "DOCTYPE" = 'DN' THEN TRY_CAST("LOCALAMOUNT" AS DOUBLE) ELSE 0 END) as dn_amt
					FROM ${arTable}
					WHERE TRY_CAST("DOCDATE" AS BIGINT) BETWEEN ${dateStart} AND ${dateEnd}
						${customerFilter}
				`),
			]);
			this.checkInterrupted(jobId);

			const tIv = Number(totalResult[0] && totalResult[0].iv_amt) || 0;
			const tCN = Number(totalResult[0] && totalResult[0].cn_amt) || 0;
			const tDN = Number(totalResult[0] && totalResult[0].dn_amt) || 0;
			const cIv = Number(customerResult[0] && customerResult[0].iv_amt) || 0;
			const cCN = Number(customerResult[0] && customerResult[0].cn_amt) || 0;
			const cDN = Number(customerResult[0] && customerResult[0].dn_amt) || 0;

			totalSalesAmount = tIv
				- (this.includeCreditNote ? tCN : 0)
				+ (this.includeDebitNote ? tDN : 0);
			customerSalesAmount = cIv
				- (this.includeCreditNote ? cCN : 0)
				+ (this.includeDebitNote ? cDN : 0);
		}

		this.salesPercentage = roundToFixed(
			totalSalesAmount !== 0 ? (customerSalesAmount / totalSalesAmount) * 100 : 0,
			this.decimal,
		);

		this.amount = this.currency
			+ ' ' + customerSalesAmount.toLocaleString()
			+ ' / ' + this.currency
			+ ' ' + totalSalesAmount.toLocaleString();

		this.generateLoadingText(1);
		this.saveHistory('salesPercentage', 'amount');
	}
}
